import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row } from 'reactstrap';
import ReactLoading from 'react-loading';
import { isEmpty } from 'lodash-es';
import axios from 'axios';

import AddLotImage from './AddLotImage';
import SortableImageContainer from './SortableImageContainer';
import ImageSizeTestBanner from '../ImageSizeTestBanner';

/**
 * This container represents everything underneath the dropzone.  Note that the
 * functionality is still handled at the parent level.
 */

class AddLotImageContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: props.files,
      lotImages: props.lotImages,
      showWarningBanner: false
    };
  }

  static getDerivedStateFromProps(props, state) {
    // The user uploaded more files or deleted some
    const stateObj = {};
    if (props.files.length !== state.files.length) {
      stateObj.files = props.files;
    }

    if (Object.keys(props.lotImages).length !== Object.keys(state.lotImages).length) {
      stateObj.lotImages = props.lotImages;
    }

    return stateObj;
  }

  handleImageSizeCheck = (imageCheck) => {
    if (imageCheck) {
      this.setState({ showWarningBanner: true });
    }
  };

  /**
   * Trigger: The user swaps two images.  Images must be already uploaded
   * and without any errors.
   *
   * Effect:  Swap the image that the user dragged into the new location.
   * Shift all images in between over by one.
   */
  onSortEnd = async ({ oldIndex, newIndex }) => {
    // User drag/dropped in the same spot, do nothing
    if (oldIndex === newIndex) {
      return;
    }

    const { catalogID, lotNumber } = this.props;
    const lotImageCopy = { ...this.state.lotImages };

    const smallerNumber = Math.min(oldIndex, newIndex);
    const largerNumber = Math.max(oldIndex, newIndex);

    const postBody = { imageOrder: {} };
    // Initialize the post body with the original values
    Object.keys(lotImageCopy).forEach(key => {
      postBody.imageOrder[key] = key;
    });

    // User dragged from right to left, slidddeee to the right
    if (newIndex < oldIndex) {
      const originalVal = lotImageCopy[largerNumber];
      for (let i = largerNumber; i > smallerNumber; i--) {
        lotImageCopy[i] = lotImageCopy[i - 1];
        postBody.imageOrder[i] = i - 1;
      }
      lotImageCopy[smallerNumber] = originalVal;
      postBody.imageOrder[smallerNumber] = largerNumber;
    } else {
      // User dragged from left to right, slidddeee to the left
      const originalVal = lotImageCopy[smallerNumber];
      for (let i = smallerNumber; i < largerNumber; i++) {
        lotImageCopy[i] = lotImageCopy[i + 1];
        postBody.imageOrder[i] = i + 1;
      }
      lotImageCopy[largerNumber] = originalVal;
      postBody.imageOrder[largerNumber] = smallerNumber;
    }

    try {
      await axios.post(`/cps/app/catalog/${catalogID}/${lotNumber}/reorderimages`, postBody, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
      // set state to new image order
      this.setState({ lotImages: lotImageCopy });
    } catch (err) {
      console.log('error resequencing images');
      console.log(err);
    }
  };

  render() {
    const { files, lotImages, showWarningBanner } = this.state;

    if (isEmpty(files) && isEmpty(lotImages)) {
      return null;
    }

    const {
      abortFile,
      addFileError,
      catalogID,
      handleDeleteFile,
      handleFileComplete,
      house: { houseID },
      isDeletingFile,
      lotNumber,
      lotData,
    } = this.props;

    if (isDeletingFile) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    // The default case since arrays start at 0
    let maxSeqNumber = -1;
    const lotImageKeys = Object.keys(lotImages);

    if (lotImageKeys.length > 0) {
      lotImageKeys.sort((a, b) => a - b);
      // If a user uploads additional images - increment from the largest sequence number
      maxSeqNumber = parseInt([...lotImageKeys].pop(), 10);
    }

    const filesWithErrors = files.filter(file => file.hasError);
    const uploadingFiles = files.filter(file => !file.hasError && !file.isComplete);

    // Row with columns for each file
    return (
      <div className="mt-4">
        <Row>
          {/* Needs to be separated due to sequencing for the reorder HOC */}
          {filesWithErrors.map((file, index) => (
            <AddLotImage
              key={index}
              abortFile={abortFile}
              addFileError={addFileError}
              catalogID={catalogID}
              file={file}
              handleDeleteFile={handleDeleteFile}
              handleFileComplete={handleFileComplete}
              houseID={houseID}
              index={index}
              lotNumber={lotNumber}
              maxSeqNumber={maxSeqNumber}
            />
          ))}
        </Row>

        {showWarningBanner && (
          <Row>
            <ImageSizeTestBanner />
          </Row>
        )}

        <SortableImageContainer
          axis="xy"
          handleDeleteFile={handleDeleteFile}
          lotImages={lotImages}
          lotImageKeys={lotImageKeys}
          maxSeqNumber={maxSeqNumber}
          onSortEnd={this.onSortEnd}
        />

        <Row>
          {lotData && uploadingFiles.map((file, index) => (
            <AddLotImage
              abortFile={abortFile}
              addFileError={addFileError}
              catalogID={catalogID}
              file={file}
              handleDeleteFile={handleDeleteFile}
              handleFileComplete={handleFileComplete}
              houseID={houseID}
              index={index}
              key={index}
              lotNumber={lotNumber}
              maxSeqNumber={maxSeqNumber}
              lotData={lotData}
              handleImageSizeCheck={this.handleImageSizeCheck}
            />
          ))}
        </Row>
      </div>
    );
  }
}

const mapStateToProps = ({ house }) => ({
  house
});

export default connect(
  mapStateToProps,
  null
)(AddLotImageContainer);
